import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Plus, Trash2, Pencil, Check, X } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { Button } from '../../components';
import { imageTrailsService } from '../../services';

const HeaderActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const FormCard = styled.div`
  background: ${props => props.theme.colors.background.secondary};
  border: 1px solid ${props => props.theme.colors.border?.light || 'rgba(0,0,0,0.1)'};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: 1.5rem;
  margin-bottom: 2rem;

  h3 {
    margin: 0 0 1rem 0;
    color: ${props => props.theme.colors.text.primary};
    font-size: ${props => props.theme.typography.body};
    font-weight: 600;
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-end;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  flex: 1;

  label {
    font-size: ${props => props.theme.typography.small};
    font-weight: 500;
    color: ${props => props.theme.colors.text.secondary};
  }

  input {
    padding: 0.6rem 0.85rem;
    border: 1px solid ${props => props.theme.colors.border?.light || 'rgba(0,0,0,0.15)'};
    border-radius: ${props => props.theme.borderRadius.sm};
    background: ${props => props.theme.colors.background.primary};
    color: ${props => props.theme.colors.text.primary};
    font-size: ${props => props.theme.typography.body};
    outline: none;
    transition: border-color 0.15s ease;

    &:focus {
      border-color: ${props => props.theme.colors.primary};
    }
  }
`;

const TrailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const TrailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: ${props => props.theme.colors.background.secondary};
  border: 1px solid ${props => props.theme.colors.border?.light || 'rgba(0,0,0,0.1)'};
  border-radius: ${props => props.theme.borderRadius.md};
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const Thumbnail = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;
  border-radius: 6px;
  background: ${props => props.theme.colors.background.tertiary};
  flex-shrink: 0;
`;

const TrailInfo = styled.div`
  flex: 1;
  min-width: 0;

  .name {
    font-weight: 600;
    color: ${props => props.theme.colors.text.primary};
    font-size: ${props => props.theme.typography.body};
  }

  .url {
    color: ${props => props.theme.colors.text.secondary};
    font-size: ${props => props.theme.typography.small};
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.text.secondary};

  h3 {
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: 0.75rem;
  }

  p {
    margin: 0;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: ${props => props.theme.colors.text.secondary};
`;

export const ImageTrailAdmin = () => {
  const [trails, setTrails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', imageUrl: '', skill: '', ctaHeading: '', ctaSubtext: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', imageUrl: '', skill: '', ctaHeading: '', ctaSubtext: '' });

  const SKILL_OPTIONS = [
    { value: '', label: 'None' },
    { value: 'manage', label: 'Manage' },
    { value: 'design', label: 'Design' },
    { value: 'animate', label: 'Animate' },
    { value: 'code', label: 'Code' },
  ];

  useEffect(() => {
    fetchTrails();
  }, []);

  const fetchTrails = async () => {
    try {
      const data = await imageTrailsService.getAllImageTrails();
      setTrails(data);
    } catch (error) {
      console.error('Error fetching image trails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    const name = form.name.trim();
    const imageUrl = form.imageUrl.trim();
    const skill = form.skill;
    const ctaHeading = form.ctaHeading.trim();
    const ctaSubtext = form.ctaSubtext.trim();
    if (!name || !imageUrl) return;

    setAdding(true);
    try {
      const id = await imageTrailsService.addImageTrail({ name, imageUrl, skill, ctaHeading, ctaSubtext });
      setTrails(prev => [...prev, { id, name, imageUrl, skill, ctaHeading, ctaSubtext }]);
      setForm({ name: '', imageUrl: '', skill: '', ctaHeading: '', ctaSubtext: '' });
    } catch (error) {
      console.error('Error adding image trail:', error);
      alert('Failed to add image trail.');
    } finally {
      setAdding(false);
    }
  };

  const handleEditStart = (trail) => {
    setEditingId(trail.id);
    setEditForm({ name: trail.name, imageUrl: trail.imageUrl, skill: trail.skill || '', ctaHeading: trail.ctaHeading || '', ctaSubtext: trail.ctaSubtext || '' });
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditForm({ name: '', imageUrl: '', skill: '', ctaHeading: '', ctaSubtext: '' });
  };

  const handleEditSave = async (id) => {
    const name = editForm.name.trim();
    const imageUrl = editForm.imageUrl.trim();
    const skill = editForm.skill;
    const ctaHeading = editForm.ctaHeading.trim();
    const ctaSubtext = editForm.ctaSubtext.trim();
    if (!name || !imageUrl) return;
    try {
      await imageTrailsService.updateImageTrail(id, { name, imageUrl, skill, ctaHeading, ctaSubtext });
      setTrails(prev => prev.map(t => t.id === id ? { ...t, name, imageUrl, skill, ctaHeading, ctaSubtext } : t));
      setEditingId(null);
    } catch (error) {
      console.error('Error updating image trail:', error);
      alert('Failed to update image trail.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this image trail config?')) return;
    try {
      await imageTrailsService.deleteImageTrail(id);
      setTrails(prev => prev.filter(t => t.id !== id));
    } catch (error) {
      console.error('Error deleting image trail:', error);
      alert('Failed to delete image trail.');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleAdd();
  };

  if (loading) {
    return (
      <AdminLayout title="Image Trail">
        <LoadingContainer><p>Loading image trails...</p></LoadingContainer>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Image Trail">
      <HeaderActions>
        <h2 style={{ margin: 0, color: 'var(--color-text-primary)' }}>
          All Configs ({trails.length})
        </h2>
      </HeaderActions>

      <FormCard>
        <h3>Add New Config</h3>
        <FormRow>
          <Field>
            <label htmlFor="trail-name">Name</label>
            <input
              id="trail-name"
              type="text"
              placeholder="e.g. halloween"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              onKeyDown={handleKeyDown}
            />
          </Field>
          <Field>
            <label htmlFor="trail-url">Image URL</label>
            <input
              id="trail-url"
              type="text"
              placeholder="https://example.com/image.png"
              value={form.imageUrl}
              onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))}
              onKeyDown={handleKeyDown}
            />
          </Field>
        </FormRow>
        <FormRow style={{ marginTop: '0.75rem' }}>
          <Field>
            <label htmlFor="trail-cta-heading">CTA Heading</label>
            <input
              id="trail-cta-heading"
              type="text"
              placeholder="Great story starts with a simple hello."
              value={form.ctaHeading}
              onChange={e => setForm(f => ({ ...f, ctaHeading: e.target.value }))}
            />
          </Field>
          <Field>
            <label htmlFor="trail-cta-subtext">CTA Subtext</label>
            <input
              id="trail-cta-subtext"
              type="text"
              placeholder="Custom message for this visitor..."
              value={form.ctaSubtext}
              onChange={e => setForm(f => ({ ...f, ctaSubtext: e.target.value }))}
            />
          </Field>
          <Field style={{ maxWidth: '160px' }}>
            <label htmlFor="trail-skill">Skill Card</label>
            <select
              id="trail-skill"
              value={form.skill}
              onChange={e => setForm(f => ({ ...f, skill: e.target.value }))}
              style={{ padding: '0.6rem 0.85rem', borderRadius: '8px', border: '1px solid #ccc', fontSize: '0.875rem', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
            >
              {SKILL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Button
            onClick={handleAdd}
            disabled={adding || !form.name.trim() || !form.imageUrl.trim()}
          >
            <Plus size={18} />
            {adding ? 'Adding...' : 'Add'}
          </Button>
        </FormRow>
      </FormCard>

      {trails.length === 0 ? (
        <EmptyState>
          <h3>No image trail configs yet</h3>
          <p>Add a config above to get started. Use <code>?co=name</code> in the URL to activate it.</p>
        </EmptyState>
      ) : (
        <TrailList>
          {trails.map(trail => (
            <TrailRow key={trail.id}>
              <Thumbnail
                src={editingId === trail.id ? editForm.imageUrl : trail.imageUrl}
                alt={trail.name}
                onError={e => { e.target.style.visibility = 'hidden'; }}
              />
              {editingId === trail.id ? (
                <TrailInfo style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Name"
                    style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.875rem' }}
                  />
                  <input
                    type="text"
                    value={editForm.imageUrl}
                    onChange={e => setEditForm(f => ({ ...f, imageUrl: e.target.value }))}
                    placeholder="Image URL"
                    style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.875rem' }}
                  />
                  <input
                    type="text"
                    value={editForm.ctaHeading}
                    onChange={e => setEditForm(f => ({ ...f, ctaHeading: e.target.value }))}
                    placeholder="CTA Heading"
                    style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.875rem' }}
                  />
                  <textarea
                    value={editForm.ctaSubtext}
                    onChange={e => setEditForm(f => ({ ...f, ctaSubtext: e.target.value }))}
                    placeholder="CTA Subtext"
                    rows={2}
                    style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.875rem', resize: 'vertical' }}
                  />
                  <select
                    value={editForm.skill}
                    onChange={e => setEditForm(f => ({ ...f, skill: e.target.value }))}
                    style={{ padding: '0.4rem 0.6rem', borderRadius: '6px', border: '1px solid #ccc', fontSize: '0.875rem', background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                  >
                    {SKILL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </TrailInfo>
              ) : (
                <TrailInfo>
                  <div className="name">{trail.name}</div>
                  <div className="url">{trail.imageUrl}</div>
                  {trail.skill && <div className="url">Skill: {trail.skill}</div>}
                  {trail.ctaHeading && <div className="url">CTA: {trail.ctaHeading}</div>}
                </TrailInfo>
              )}
              {editingId === trail.id ? (
                <>
                  <Button variant="ghost" size="sm" onClick={() => handleEditSave(trail.id)} style={{ color: '#6B8E23', flexShrink: 0 }}>
                    <Check size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleEditCancel} style={{ flexShrink: 0 }}>
                    <X size={14} />
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" size="sm" onClick={() => handleEditStart(trail)} style={{ flexShrink: 0 }}>
                    <Pencil size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(trail.id)} style={{ color: 'var(--color-error)', flexShrink: 0 }}>
                    <Trash2 size={14} />
                  </Button>
                </>
              )}
            </TrailRow>
          ))}
        </TrailList>
      )}
    </AdminLayout>
  );
};
